import {
  IResourceComponentsProps,
  useCreate,
  useNavigation,
  useNotification,
  usePermissions,
  useUpdate,
} from "@refinedev/core";
import { Edit, SaveButtonProps } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";

import { Avatar, Box, MenuItem, TextField } from "@mui/material";

import { MuiFileInput } from "mui-file-input";

import { userTypeOptions } from ".";
import { commonValues } from "constants/common.values";
import { QueryKeys } from "constants/keys";
import { queryClient } from "App";

import { FileEntity, UserEntity, UserEntityRoleEnum } from "generated/api";

export const availabilityOptions: { label: string; value: string }[] = [
  {
    label: "True",
    value: "true",
  },
  {
    label: "False",
    value: "false",
  },
];

export const EditUserInfo = () => {
  const {
    refineCore: { queryResult, formLoading },
    register,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm();

  const { mutate, isLoading } = useUpdate();

  const { mutateAsync, isLoading: isCreating } = useCreate();

  const { open } = useNotification();

  const { list } = useNavigation();

  const uploadImage = async (
    image: File
  ): Promise<{
    image?: string;
  }> => {
    let error = "";

    try {
      const formData = new FormData();
      formData.append("files", image);
      const responseData = (
        await mutateAsync({
          resource: "files",
          values: formData,
        })
      ).data as FileEntity[] | undefined;

      if (responseData?.[0]?.url) {
        return { image: responseData[0].url };
      } else {
        error = responseData?.[0].error || "";
      }
    } catch (err) {
      error = (err as Error).message;
    }

    if (error) {
      open?.({
        type: "error",
        message: "Error uploading file: " + error,
      });
    }
    return {};
  };

  const saveButtonProps: SaveButtonProps = {
    onClick: handleSubmit(async (d) => {
      let imageUrl: string | undefined;
      if (d.image) {
        const { image } = await uploadImage(d.image);
        imageUrl = image;
      }

      mutate(
        {
          resource: "users",
          id: queryResult?.data?.data.id || "",
          values: { ...d, image: imageUrl || undefined },
          meta: {
            method: "put",
          },
          successNotification: (_, values) => {
            return {
              description: "Account edited",
              //@ts-ignore
              message: `${values?.values.role} successfully updated.`,
              type: "success",
            };
          },
          errorNotification: (errors) => {
            return {
              description: errors?.message?.toUpperCase?.(),
              message: errors?.errors?.length
                ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
                : "",
              type: "error",
            };
          },
        },
        {
          onSuccess: () => list("users"),
        }
      );
    }),
    disabled: isLoading || isCreating,
  };

  return (
    <Edit isLoading={formLoading} saveButtonProps={saveButtonProps}>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        {queryResult?.data?.data?.image ? (
          <Avatar
            src={queryResult?.data?.data?.image}
            sx={{
              height: "100px",
              width: "100px",
              marginX: "auto",
            }}
          />
        ) : null}

        <TextField
          {...register("name", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.name}
          helperText={(errors as any)?.name?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Name"
          name="name"
        />

        <TextField
          {...register("email", {
            required: "This field is required",
            // eslint-disable-next-line no-useless-escape
            pattern: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
          })}
          error={!!(errors as any)?.email}
          helperText={(errors as any)?.email?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="email"
          label="Email"
          name="email"
        />

        <TextField
          {...register("phoneNumber", {
            required: "This field is required",
            pattern: /^[0-9]{10}$/,
          })}
          error={!!(errors as any)?.phoneNumber}
          helperText={(errors as any)?.phoneNumber?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          label="Phone Number"
          name="phoneNumber"
        />

        <MuiFileInput
          label="Image"
          name="image"
          multiple={false}
          placeholder={"Select an image"}
          inputProps={{
            accept: "image/*",
          }}
          value={watch("image")}
          onChange={(file) => {
            if (!file) {
              setValue("image", undefined);
              return;
            }

            const hasExceededSize =
              file.size / (1024 * 1024) > commonValues.maxFileSize;

            if (hasExceededSize) {
              open?.({
                type: "error",
                message: `Max file size is ${commonValues.maxFileSize} MB`,
              });
              return;
            }

            setValue("image", file);
          }}
        />

        {queryResult?.data?.data.role ? (
          <TextField
            {...register("role", {
              required: "This field is required",
            })}
            error={!!(errors as any)?.role}
            helperText={(errors as any)?.role?.message}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="Account Type"
            name="role"
            select
            disabled
            defaultValue={queryResult?.data?.data.role}
          >
            {userTypeOptions.map(({ label, value }, i) => (
              <MenuItem key={label + i} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </Box>
    </Edit>
  );
};

export const ToggleAvailablity = () => {
  const {
    refineCore: { queryResult, formLoading },
    register,
    formState: { errors },
    handleSubmit,
  } = useForm();

  const { mutate, isLoading } = useCreate();

  const { list } = useNavigation();

  const saveButtonProps: SaveButtonProps = {
    onClick: handleSubmit((d) => {
      mutate(
        {
          resource: `auth/collector/${
            d.enabled === "true" ? "enable" : "disable"
          }`,
          values: {
            collectorId: queryResult?.data?.data.id,
          },
          successNotification: (_, values) => {
            return {
              description: "Account updated",
              //@ts-ignore
              message: `COLLECTOR successfully updated.`,
              type: "success",
            };
          },
          errorNotification: (errors) => {
            return {
              description: errors?.message?.toUpperCase?.(),
              message: errors?.errors?.length
                ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
                : "",
              type: "error",
            };
          },
        },
        {
          onSuccess: () => list("users"),
        }
      );
    }),
    disabled: isLoading,
  };

  return queryResult?.data?.data?.role === UserEntityRoleEnum.Admin ? null : (
    <Box mt={2}>
      <Edit
        title={"Change Collector availability"}
        isLoading={formLoading}
        saveButtonProps={saveButtonProps}
      >
        {typeof queryResult?.data?.data?.enabled === "boolean" ? (
          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column" }}
            autoComplete="off"
          >
            <TextField
              {...register("enabled", {
                required: "This field is required",
              })}
              error={!!(errors as any)?.enabled}
              helperText={(errors as any)?.enabled?.message}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Account Enabled"
              name="enabled"
              select
              defaultValue={
                queryResult?.data?.data.enabled === true ? "true" : "false"
              }
            >
              {availabilityOptions.map(({ label, value }, i) => (
                <MenuItem key={label + i} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        ) : null}
      </Edit>
    </Box>
  );
};

export const ChangePassword = () => {
  const {
    refineCore: { queryResult, formLoading },
    register,
    formState: { errors },
    handleSubmit,
  } = useForm();

  const { mutate, isLoading } = useCreate();

  const { list } = useNavigation();

  const userData = queryClient.getQueryData([QueryKeys.ME_QUERY]) as
    | UserEntity
    | undefined;

  const isOwnAccount = userData && userData.id === queryResult?.data?.data?.id;

  const saveButtonProps: SaveButtonProps = {
    onClick: handleSubmit((data) => {
      mutate(
        {
          resource: "auth/password/change",
          meta: {
            method: "post",
          },
          values: data,
          successNotification: (_, values) => {
            return {
              description: "Password updated",
              message: `Password successfully updated.`,
              type: "success",
            };
          },
          errorNotification: (errors) => {
            return {
              description: errors?.message?.toUpperCase?.(),
              message: errors?.errors?.length
                ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
                : "",
              type: "error",
            };
          },
        },
        {
          onSuccess: () => list("users"),
        }
      );
    }),
    disabled: isLoading,
  };

  return isOwnAccount ? (
    <Box mt={2}>
      <Edit
        title={"Change Password"}
        isLoading={formLoading}
        saveButtonProps={saveButtonProps}
      >
        <Box
          component="form"
          sx={{ display: "flex", flexDirection: "column" }}
          autoComplete="off"
        >
          <TextField
            {...register("curPassword", {
              required: "This field is required",
            })}
            error={!!(errors as any)?.curPassword}
            helperText={(errors as any)?.curPassword?.message}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="Current Password"
            name="curPassword"
            type="password"
          />

          <TextField
            {...register("newPassword", {
              required: "This field is required",
            })}
            error={!!(errors as any)?.newPassword}
            helperText={(errors as any)?.newPassword?.message}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="New Password"
            name="newPassword"
            type="password"
          />
        </Box>
      </Edit>
    </Box>
  ) : null;
};

export const ResetPassword = () => {
  const {
    refineCore: { queryResult, formLoading },
    register,
    formState: { errors },
    handleSubmit,
  } = useForm();

  const { mutate, isLoading } = useCreate();

  const { list } = useNavigation();

  const permission = usePermissions();
  const isAdmin = permission?.data === UserEntityRoleEnum.Admin;

  const saveButtonProps: SaveButtonProps = {
    onClick: handleSubmit((data) => {
      mutate(
        {
          resource: "auth/collector/reset-pwd",
          meta: {
            method: "post",
          },
          values: { ...data, collectorId: queryResult?.data?.data?.id },
          successNotification: (_, values) => {
            return {
              description: "Password reset",
              message: `Password successfully reset.`,
              type: "success",
            };
          },
          errorNotification: (errors) => {
            return {
              description: errors?.message?.toUpperCase?.(),
              message: errors?.errors?.length
                ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
                : "",
              type: "error",
            };
          },
        },
        {
          onSuccess: () => list("users"),
        }
      );
    }),
    disabled: isLoading,
  };

  return queryResult?.data?.data?.role !== UserEntityRoleEnum.Admin &&
    isAdmin ? (
    <Box mt={2}>
      <Edit
        title={"Reset Password"}
        isLoading={formLoading}
        saveButtonProps={saveButtonProps}
      >
        <Box
          component="form"
          sx={{ display: "flex", flexDirection: "column" }}
          autoComplete="off"
        >
          <TextField
            {...register("newPassword", {
              required: "This field is required",
            })}
            error={!!(errors as any)?.newPassword}
            helperText={(errors as any)?.newPassword?.message}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="New Password"
            name="newPassword"
            type="password"
          />
        </Box>
      </Edit>
    </Box>
  ) : null;
};

export const UserEdit: React.FC<IResourceComponentsProps> = () => {
  return (
    <>
      <EditUserInfo />
      <ToggleAvailablity />
      <ChangePassword />
      <ResetPassword />
    </>
  );
};
